import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef} from 'react';

import {WYSIWYGEditor} from '../../slack';
import {ContentCard, ContentToolbar} from '../components';
import {createDefaultContent, getEditorProps, getParagraphEditorValue} from './utils';
import {
  useCreateBlock,
  useUpdateBlockContentJson,
} from '../hooks';
import {BaseBlock, ContentActions, ContentOptions} from '../types';
import {defaultContentActions, defaultContentOptions} from '../constants';

import './Paragraph.scss';

interface ParagraphProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const Paragraph = forwardRef(({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: ParagraphProps, ref: any) => {
  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const editorRef = useRef<any>();
  const uid = content.uid;

  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  const editorProps = useMemo(() => getEditorProps(), []);

  useCreateBlock(content, createDefaultContent());

  const onValueChange = (value: any) => {
    const {html, markdown} = value;

    const child = {
      text: markdown,
      html,
    };

    updateBlockContentJson('children', [child]);
  };

  useImperativeHandle(ref, () => ({
    reInitEditor() {
      if (!editorRef.current) {
        return;
      }
      editorRef.current.reInitEditor();
    },
  }));

  /*
   * TODO: fix bug with "losing content" on drag end (on drop)
   */
  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={content.uid}
      disabled={options.readOnly}
      noContentElement={undefined}
      allowCollapse={options.allowCollapse}
      showHeader={false}
      showFooter={false}
    >
      <div className="content__paragraph">
        <ContentToolbar
          show={options?.showToolbar}
          contentId={uid as string}
          updating={updating}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false}
          disabled={options.readOnly || false}
          showSelect={false}
          showPreview={false}
          showDuplicate={false}
        />
        <div className="content-card__content__form">
          <div className="editor">
            <WYSIWYGEditor
              ref={editorRef}
              defaultValue={getParagraphEditorValue(content)}
              onChange={onValueChange}
              debounceTime={800}
              editorProps={editorProps}
              disabled={options.readOnly}
            />
          </div>
        </div>
      </div>
    </ContentCard>
  );
});
